import { DeferredPromise } from "DeferredPromise";
import {
  bindingHandlers,
  bindingProvider,
  computed,
  expressionRewriting,
  extenders,
  ignoreDependencies,
  isObservable,
  observable,
  observableArray,
  pureComputed,
  subscribable,
  unwrap,
  utils,
  virtualElements,
  type Computed,
  type Observable,
  type ObservableArrayFunctions,
  type PureComputed,
  type RateLimitOptions,
  type Subscribable,
} from "knockout.base";

export interface PausableObservable<T> extends Observable<T> {
  poke(newValue: T): void;
}

export interface ExtendedObservable<T> extends Observable<T> {
  withPausing(): PausableObservable<T>;
}

export interface ExtendedComputed<T> extends Computed<T> {
  loaded: Observable<boolean | undefined>;
  refresh(): void;
  notifySubscribersImmediately(): void;
}

export interface AsyncComputedValueLoaderResult<T> {
  readonly syncValue: T;
  readonly asyncPromise?: Promise<T | undefined>;
  readonly errorHandler?: (error: Error) => boolean;
}

export interface AsyncComputed<T> extends Computed<T> {
  loaded: () => boolean;
  state: Observable<PromiseState>;
  waitForLoadAsync: () => Promise<void | null>;
}

/*! SuppressStringValidation Promise states */
export type PromiseState = "pending" | "rejected" | "resolved";

export function lazyObservable<TValue, TContext>(
  callback: (this: TContext | undefined, value?: TValue) => void,
  context?: TContext,
  initialValue?: TValue,
): ExtendedComputed<TValue> {
  const value = observable(initialValue);
  return lazyComputed(callback, value, context);
}

export function lazyComputed<TValue, TContext>(
  callback: (this: TContext | undefined, value?: TValue) => void,
  value: Observable<TValue | undefined>,
  context?: TContext,
): ExtendedComputed<TValue> {
  const result = computed({
    read() {
      //if it has not been loaded, execute the supplied function
      if (!result.loaded.peek()) {
        ignoreDependencies(() => {
          callback.call(context, value());
        });
      }
      //always return the current value
      return value();
    },
    write(newValue: TValue | undefined) {
      //indicate that the value is now loaded and set it
      result.loaded(true);
      value(newValue);
    },
    deferEvaluation: true, //do not evaluate immediately when created
  }) as ExtendedComputed<TValue>;

  //expose the current state, which can be bound against
  result.loaded = observable<boolean>();

  //load it again
  result.refresh = (): void => {
    result.loaded(false);
    callback.call(context);
  };

  return result;
}

export function lazyObservableArray<T, TContext = unknown>(
  callback: () => Promise<void>,
  context?: TContext,
): ExtendedComputed<T> & ObservableArrayFunctions<T> {
  const value = observableArray<T>();
  const result = lazyComputed(callback, value, context) as ExtendedComputed<T> & ObservableArrayFunctions<T>;

  //add underlying array methods onto computed observable
  /*! StartNoStringValidationRegion Array methods */
  type WriteableProps = Omit<ObservableArrayFunctions, "length" | "name">;
  const arrayMethods: (keyof WriteableProps)[] = [
    "remove",
    "removeAll",
    "destroy",
    "destroyAll",
    "indexOf",
    "replace",
    "pop",
    "push",
    "reverse",
    "shift",
    "sort",
    "splice",
    "unshift",
    "slice",
  ];
  /*! EndNoStringValidationRegion */
  utils.arrayForEach(arrayMethods, (methodName) => {
    result[methodName] = function (...args: T[]): void {
      return value[methodName](...args);
    };
  });

  return result;
}

type UnwrapObservableRecursive<T> = T extends Observable<infer U> ? UnwrapObservableRecursive<U> : T;
type UnwrapObservable<T> = T extends Observable<infer U> ? U : T;
const maxUnwrapRecursionDepth = 2;

export function recursiveUnwrap<T>(
  propertyValue: T,
  recursionDepth: number,
): UnwrapObservableRecursive<T> | UnwrapObservable<T> | T {
  if (recursionDepth < maxUnwrapRecursionDepth) {
    const result = unwrap(propertyValue);

    return isObservable(result) ? recursiveUnwrap(result, recursionDepth + 1) : result;
  }

  return propertyValue;
}

export async function waitForValueAsync<TValue>(
  observable: Observable<TValue> | PureComputed<TValue> | TValue,
  targetValue?: TValue,
): Promise<TValue> {
  if (!isObservable(observable) || !pureComputed(observable)) {
    return observable as TValue;
  }

  const value = observable.peek();
  const isTargetValueSpecified = typeof targetValue !== "undefined";
  if (value !== null && typeof value !== "undefined" && (!isTargetValueSpecified || value === targetValue)) {
    return value;
  }

  const deferred = new DeferredPromise<TValue>();
  const subscription = observable.subscribe((value: TValue) => {
    if (value !== null && typeof value !== "undefined" && (!isTargetValueSpecified || value === targetValue)) {
      deferred.resolve(value);
    }
  });

  try {
    return await deferred.promise;
  } finally {
    subscription.dispose();
  }
}

export function withPausing<T>(observable: Observable<T>): PausableObservable<T> {
  const pausable = observable as PausableObservable<T>;
  let pauseNotifications = false;

  observable.notifySubscribers = function notifySubscribersWithPausing(valueToWrite?: T, event?: string): void {
    if (!pauseNotifications) {
      subscribable.fn.notifySubscribers.call(this, valueToWrite, event);
    }
  };

  pausable.poke = function poke(newValue: T): void {
    pauseNotifications = true;
    this(newValue);
    pauseNotifications = false;
  };

  return pausable;
}

export function promiseObserver<T>(promise: Promise<T>): PromiseObserver<T> {
  return new PromiseObserver<T>(promise);
}

export function preprocessNode(node: Element): void {
  if (bindingProvider.instance.nodeHasBindings(node)) {
    const bindingString = getBindingsString(node);
    if (bindingString) {
      const keyValuePairs = expressionRewriting.parseObjectLiteral(bindingString);
      validateAgainstBindingHandlers(keyValuePairs);
    }
  }
}

/*! SuppressStringValidation ko binding attribute name */
const defaultBindingAttributeName = "data-bind";
/*! SuppressStringValidation ko binding keyword */
const bubbleKeyword = "Bubble";
/*! SuppressStringValidation default ko binding handler name */
const click = "click";
/*! SuppressStringValidation custom binding handler name */
const asyncClick = "asyncClick";

function validateAgainstBindingHandlers(keyValuePairs: expressionRewriting.KeyValue[]): void {
  keyValuePairs.forEach((keyValuePair) => {
    let bindingName = keyValuePair.key || keyValuePair.unknown;

    if (bindingName && !isInWhiteList(bindingName)) {
      if (bindingName.endsWith(bubbleKeyword) && !bindingHandlers[bindingName]) {
        const originalBindingName = bindingName.split(bubbleKeyword)[0];

        if (!hasOriginalBindingName(keyValuePairs, originalBindingName)) {
          throw new Error(bindingName + " must have binding attribute " + originalBindingName);
        }
        bindingName = originalBindingName;
      }
      throwIfCustomBindingNotRegistered(bindingName);
    }
  });
}

function throwIfCustomBindingNotRegistered(bindingName: string): void {
  if (bindingHandlers[bindingName] === undefined) {
    throw new Error("Custom binding is not registered: " + bindingName);
  }
}

function hasOriginalBindingName(keyValuePairs: expressionRewriting.KeyValue[], originalBindingName: string): boolean {
  let result = keyValuePairs.find((pair) => {
    return pair.key === originalBindingName;
  });
  if (!result && originalBindingName === click) {
    result = keyValuePairs.find((pair) => {
      return pair.key === asyncClick;
    });
  }
  return !!result;
}

function getBindingsString(node: Element): string | null {
  switch (node.nodeType) {
    case 1:
      return node.getAttribute(defaultBindingAttributeName); // Element
    case 8:
      return virtualElements.virtualNodeBindingValue(node); // Comment node
    default:
      return null;
  }
}

function isInWhiteList(bindingName: string): boolean {
  return bindingNameWhiteList.has(bindingName);
}

/*! StartNoStringValidationRegion ko binding options which don't have a handler */
const bindingNameWhiteList = new Set([
  "optionsCaption",
  "optionsValue",
  "optionsText",
  "optionsAfterRender",
  "valueAllowUnset",
  "valueUpdate",
  "omitBlankElement",
  "gwReadOnlyCss",
  "gwDataGridColumnType",
  "gwCaptionOptions",
  "gwValidationTooltipOptions",
]);
/*! EndNoStringValidationRegion */

export function asyncComputed<T, TOwner>(
  newValueLoader: (oldValue?: T) => AsyncComputedValueLoaderResult<T>,
  dependencyAccessor?: () => void,
  owner?: TOwner,
): AsyncComputed<T> {
  let isCommitting = false;
  const value = observable<T>();
  value.equalityComparer = equalityComparer;
  let loaderToken: object | null = null;

  const commit = (loadedValue: T | undefined): void => {
    isCommitting = true;
    value(loadedValue);
    isCommitting = false;
  };

  let promise: Promise<void | null> | null;

  const computedObservable = computed({
    read() {
      if (isCommitting) {
        if (dependencyAccessor) {
          // Keep subscriptions alive so that the computed value will be refreshed when they change.
          dependencyAccessor.call(owner);
        }
      } else {
        const oldValue = value();
        const result = newValueLoader.call(owner, oldValue);

        if (result) {
          commit(result.syncValue);
          loaderToken = null;

          if (result.asyncPromise) {
            const token = {};
            loaderToken = token;
            computedObservable.state("pending");

            promise = (async function promiseAsync(): Promise<void | null> {
              try {
                const loadedValue = await result.asyncPromise;
                if (token === loaderToken) {
                  commit(loadedValue);
                  computedObservable.state("resolved");
                  loaderToken = null;
                }

                return null;
              } catch (e) {
                const error = e as Error;
                if (token === loaderToken) {
                  let errorHandled = false;

                  computedObservable.state("rejected");
                  if (result.errorHandler) {
                    errorHandled = result.errorHandler.call(owner, error);
                  }

                  loaderToken = null;

                  if (!errorHandled) {
                    throw error;
                  }
                }
              } finally {
                if (token === loaderToken) {
                  promise = null;
                }
              }
            })();
          } else {
            computedObservable.state("resolved");
          }
        }
      }

      return value();
    },
    owner,
    deferEvaluation: true,
  }) as AsyncComputed<T>;
  computedObservable.equalityComparer = equalityComparer;
  computedObservable.state = observable<PromiseState>("pending");

  computedObservable.loaded = pureComputed(() => {
    return computedObservable.state() === "resolved";
  });

  computedObservable.waitForLoadAsync = (): Promise<void | null> => {
    return Promise.resolve(promise);
  };

  return computedObservable;
}

function equalityComparer<T>(first: T, second: T): boolean {
  return first === second;
}

const originalRateLimit = extenders.rateLimit;
export function rateLimit<T>(target: Subscribable<T>, options: number | RateLimitOptions): Subscribable<T> {
  const t = target as ExtendedComputed<T>;
  t.notifySubscribersImmediately = t.notifySubscribers;
  return originalRateLimit(t, options);
}

// A lightweight object that can be used to refresh a computed or binding when a given promise is resolved.
class PromiseObserver<T> {
  private observable: Observable<T | undefined> | undefined;
  private state: PromiseObserverState<T>;

  constructor(promise: Promise<T>) {
    this.observable = undefined;
    this.state = { type: StateType.Promise, promise: this.setUpPromiseAsync(promise) };
  }

  hasError(): boolean {
    return this.state.type === StateType.Error;
  }

  read(): T | undefined {
    const { state } = this;
    switch (state.type) {
      case StateType.Error:
        return undefined;
      case StateType.Promise: {
        let targetObservable = this.observable;
        if (!targetObservable) {
          this.observable = targetObservable = observable<T | undefined>(undefined);
        }
        return targetObservable();
      }
      case StateType.Value:
        return state.value;
    }
  }

  async getValueAsync(): Promise<T> {
    const { state } = this;
    switch (state.type) {
      case StateType.Error:
        throw state.error;
      case StateType.Promise:
        return await state.promise;
      case StateType.Value:
        return state.value;
    }
  }

  private async setUpPromiseAsync(promise: Promise<T>): Promise<T> {
    try {
      const value = await promise;
      this.state = { type: StateType.Value, value };

      const observable = this.observable;
      if (observable) {
        this.observable = undefined;
        observable(value);
      }

      return value;
    } catch (error: unknown) {
      this.state = { type: StateType.Error, error };
      throw error;
    }
  }
}

type PromiseObserverState<T> =
  | { type: StateType.Error; error: unknown }
  | { type: StateType.Promise; promise: Promise<T> }
  | { type: StateType.Value; value: T };

enum StateType {
  Error,
  Promise,
  Value,
}
